import { useCallback, useMemo, useState } from "react"
import { numberFormat } from "shared/util";

import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography
} from "@mui/material"
import {
	ContentCopy as CsvCopyIcon
 } from '@mui/icons-material';
 import { CloseIcon, DownloadIcon } from "shared/icons";

 import { ValueSpan } from "components/controls/valueSpan";
 
 import { DownLoadData } from "../types"
import { clipCsvData, downloadData } from "../"

const CsvTableHeaderCell = ({
	name
}: {
	name: string
}) => {
	return (
		<TableCell
			component="th"
			sx={{
				whiteSpace: 'nowrap',
				overflow: 'hidden',
				textOverflow: 'ellipsis'
			}}
		>
			{name}
		</TableCell>
	)
}

const CsvTableCell = ({
	value,
	unit,
	expand
}: {
	value: string | number | undefined,
	unit: string | undefined,
	expand?: boolean
}) => {
	const text = useMemo(
		() => typeof value === 'number' ? numberFormat(value) : value,
		[value]
	)
	const textAlign = useMemo(
		() => typeof value === 'number' ? 'right' : undefined,
		[value]
	)
	return (
		<TableCell
			component="td"
			width={expand ? "100%" : undefined}
			sx={{
				whiteSpace: 'nowrap',
				textAlign: textAlign
			}}
		>
			{unit ? <ValueSpan text={text} suffix={unit}/> : text}
		</TableCell>
	)
}

type CsvTableDialogProps = {
	title: string
	data: DownLoadData
	onClose: () => void
}
export const CsvTableDialog = ({
	title,
	data,
	onClose
}: CsvTableDialogProps) => {
	const [message, setMessage] = useState<string | null>(null)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)

	const onCsvCopy = useCallback(
		async () => {
			try {
				setErrorMessage(null)
				setMessage(null)

				if (!await clipCsvData(data)) {
					setErrorMessage('データのコピーに失敗しました')
					return
				}
				setMessage(`${data.body.length}件のデータをコピーしました`)
			} catch (e) {
				console.error('データのコピーに失敗', e)
				setErrorMessage('データのコピーに失敗しました')
			}
		},
		[data, setErrorMessage, setMessage]
	)
	const onExcelDownload = useCallback(
		async () => {
			try {
				const result = await downloadData({
					data,
					format: 'excel',
					fileName: title
				})
				if (result) {
					onClose()
				} else {
					setErrorMessage('データのダウンロードに失敗しました')
				}
			} catch (e) {
				console.error('データのダウンロードに失敗', e)
				setErrorMessage('データのダウンロードに失敗しました')
			}
		},
		[data, title, onClose]
	)
	return (
    <Dialog
			open
			maxWidth="lg"
			scroll="paper"
			onClose={(_, reason) => {
        if (reason === 'backdropClick') return
        onClose()
      }}
    >
      <DialogTitle>
				<Stack direction="row" spacing={1} alignItems="center">
					<Typography
						variant="body1"
						textAlign="center"
						color="text.primary"
						flexGrow={1}
						overflow="hidden"
						textOverflow="ellipsis"
						whiteSpace="nowrap"
					>
						{title}
					</Typography>
		      <Tooltip title="閉じる">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{
					padding: 0,
					overflow: "hidden"
				}}
			>
				<TableContainer sx={{ maxHeight: '70vh', minWidth: '30vw' }}>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								{data.header.map((column, i) => (
									<CsvTableHeaderCell
										key={`csv-table-column-${i}`}
										name={column.title ?? `column${i}`}
									/>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{data.body.map((row, i) => (
								<TableRow
									key={`csv-table-row-${i}`}
									sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
								>
									{data.header.map((column, j) => (
										<CsvTableCell
											expand={j==0}
											key={`csv-table-cell-${i}-${j}`}
											value={row[column.key ?? j]}
											unit={column.unit}
										/>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>

				{errorMessage && <Alert severity="error">{errorMessage}</Alert>}
			</DialogContent>

			<DialogActions
				sx={{
					justifyContent: 'flex-end',
					padding: 1
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center">
					<Box flexGrow={1}>
						{message && <Alert severity="success">{message}</Alert>}
					</Box>
					<Stack direction="row" flexShrink={0} spacing={2} padding={1} alignItems="center">
						<Button
							variant="contained"
							onClick={onCsvCopy}
							startIcon={<CsvCopyIcon/>}
						>
							コピー
						</Button>
						<Button
							variant="contained"
							onClick={onExcelDownload}
							startIcon={<DownloadIcon/>}
						>
							ダウンロード
						</Button>
						<Button
							variant="text"
							onClick={onClose}
							startIcon={<CloseIcon/>}
						>
							閉じる
						</Button>
					</Stack>
				</Stack>
			</DialogActions>
		</Dialog>
	)
}
